import { ColDef, ICellRendererParams } from "ag-grid-community";
import {
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  Stack,
  Tooltip,
} from "@mui/material";
import { FC, Fragment, useCallback } from "react";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useAppSelector } from "@/store/hooks";
import { useMenuHook, UseMenuHookOpenMenu } from "@/utils/hooks/useMenu.hook";
import { Protection } from "@/components/pages/live-usage-mgmt/components/resource-card/resource-card-content/resource-card-data/resource-card-grid/resouce-card-data-grid/cells/actions-menu-cell-v2/Protection";
import { UpcomingActionIcon } from "@/components/pages/live-usage-mgmt/components/resource-card/resource-card-content/resource-card-data/resource-card-grid/resouce-card-data-grid/cells/actions-menu-cell-v2/UpcomingAction";
import { LiveUsageResourceGridToolbarCleanActions } from "@/components/pages/live-usage-mgmt/components/resources-area/toolbar/actions/clean/LiveUsageResourceGridToolbarCleanActions";
import { LiveUsageResourceGridProtectAction } from "@/components/pages/live-usage-mgmt/components/resources-area/toolbar/actions/LiveUsageResourceGridProtectAction";
import { masterGridResourceTypeSelector } from "@/store/resources-grid/selectors/grid/masterGridResourceTypeSelector";
import { IntegrationMenuItemsV2 } from "@/components/pages/live-usage-mgmt/components/resource-card/resource-card-content/resource-card-data/resource-card-grid/resouce-card-data-grid/cells/actions-menu-cell-v2/menu-items/integration-menu-items/IntegrationMenuItemsV2";
import { LiveUsageResourceGridCreateTaskRowAction } from "@/components/pages/live-usage-mgmt/components/resources-area/toolbar/actions/task-create/LiveUsageResourceGridCreateTaskRowAction";
import { LiveUsageResourceGridFilterOutAction } from "@/components/pages/live-usage-mgmt/components/resources-area/toolbar/actions/LiveUsageResourceGridFilterOutAction";
import { LiveUsageResourceGridCreateWorkflowAction } from "@/components/pages/live-usage-mgmt/components/resources-area/toolbar/actions/LiveUsageResourceGridCreateWorkflowAction";
import { isResourceKubernetes } from "@/store/resources-grid/utils/helpers/isResourceKubernetes";
import { agGridActionColumnDefaultColumnDef } from "@/components/pages/common/ag-grid/utils/constants/agGridActionColumnDefaultColumnDef";

interface CellRendererProps extends ICellRendererParams {
  masterGridId: string;
}

const CellRenderer: FC<CellRendererProps> = ({ data, masterGridId, node }) => {
  const id = data.id;
  const resourceType = data?.resource_type;
  const isProtected = data?.resource?.is_protected;
  const inProgress = data?.resource?.marked_for_deletion;
  const failureReason = data?.resource?.cleanup_failure_reason;
  const uniqueIdentifier = data?.resource?.provider_unique_identifier;
  const upcomingActions = data?.resource?.upcoming_actions;
  const protectedBy = data?.resource?.protected_by;
  const account = data?.resource?.account;
  const resourceHasTask = !!data?.resource?.task?.id;
  const isKubernetes = isResourceKubernetes(data?.resource);

  const masterResourceType = useAppSelector((state) =>
    masterGridResourceTypeSelector(state, masterGridId),
  );

  const { anchor, open, openMenu, closeMenu } = useMenuHook();

  const openMenuHandler: UseMenuHookOpenMenu = useCallback(
    (e) => {
      node.setSelected(true);
      openMenu(e);
    },
    [node, openMenu],
  );

  const closeHandler = useCallback(() => {
    node.setSelected(false);
    closeMenu();
  }, [node, closeMenu]);

  if (!masterResourceType) {
    return null;
  }

  return (
    <Fragment>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.4}
        justifyContent="flex-end"
      >
        {inProgress && (
          <Tooltip title="Action in progress..." arrow>
            <CircularProgress
              size={20}
              color="warning"
              sx={{ verticalAlign: "middle", color: "info.main" }}
            />
          </Tooltip>
        )}

        {isProtected && <Protection protectedBy={protectedBy} />}

        {failureReason && !inProgress && (
          <Tooltip title={failureReason} arrow sx={{ display: "inline" }}>
            <WarningAmberIcon fontSize="small" color="error" />
          </Tooltip>
        )}

        <UpcomingActionIcon upcomingActions={upcomingActions} />

        <IconButton
          size="small"
          onClick={openMenuHandler}
          disabled={inProgress}
          sx={{ p: 0 }}
        >
          <MoreVertIcon />
        </IconButton>
      </Stack>

      <Menu anchorEl={anchor} open={open} onClose={closeMenu}>
        {!isKubernetes && (
          <LiveUsageResourceGridToolbarCleanActions
            onClose={closeMenu}
            masterGridId={masterGridId}
            masterResourceType={masterResourceType}
          />
        )}

        <IntegrationMenuItemsV2
          id={id}
          onSelect={closeMenu}
          resourceType={resourceType}
          isKubernetes={isKubernetes}
        />

        {!isKubernetes && (
          <LiveUsageResourceGridCreateTaskRowAction
            onClose={closeHandler}
            masterGridId={masterGridId}
            resourceHasTask={resourceHasTask}
          />
        )}

        {!isKubernetes && (
          <LiveUsageResourceGridFilterOutAction
            inRowAction
            onClose={closeHandler}
            masterGridId={masterGridId}
          />
        )}

        {!isKubernetes && <Divider />}

        {!isKubernetes && (
          <LiveUsageResourceGridCreateWorkflowAction
            account={account}
            onClose={closeHandler}
            masterGridId={masterGridId}
            uniqueIdentifier={uniqueIdentifier}
          />
        )}

        {!isKubernetes && (
          <LiveUsageResourceGridProtectAction
            onClose={closeHandler}
            masterGridId={masterGridId}
          />
        )}
      </Menu>
    </Fragment>
  );
};

export const getResourceGridResourceInfoWithActionsColumnDef = (
  masterGridId: string,
): ColDef => ({
  ...agGridActionColumnDefaultColumnDef,
  colId: "resourceInfoWithActionsColDefId",
  minWidth: 60,
  maxWidth: 100,
  headerName: "",
  cellRenderer: (props: ICellRendererParams) => {
    return <CellRenderer {...props} masterGridId={masterGridId} />;
  },
});
